
import { useEffect, useMemo, useRef, useState, } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, } from 'react-router-dom';
import { fetchBillById } from '../store';

const ViewExcel = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    if (id) {
      dispatch(fetchBillById(id));
    }
  }, [id, dispatch]);

  const dataExcel = useSelector((state) => state.excel);
  const { data, loading } = dataExcel;

  const mergedCellsMap = useMemo(() => {
    if (!data?.merges) return new Map();

    const map = new Map();
    data.merges.forEach((merge) => {
      const { startRow, startCol, endRow, endCol, rowSpan, colSpan } = merge;

      const mainKey = `${startRow}-${startCol}`;
      map.set(mainKey, {
        isMerged: true,
        rowSpan: rowSpan,
        colSpan: colSpan,
      });

      for (let row = startRow; row <= endRow; row++) {
        for (let col = startCol; col <= endCol; col++) {
          const key = `${row}-${col}`;
          if (row !== startRow || col !== startCol) {
            map.set(key, { skip: true });
          }
        }
      }
    });

    return map;
  }, [data?.merges]);

  // ✅ Convert ARGB (Excel) sang CSS color
  const argbToRgb = (argb) => {
    if (!argb || typeof argb !== 'string') return null;

    const hex = argb.replace(/^(FF|ff)/, '');

    if (hex.length === 6) {
      return `#${hex}`;
    }
    return null;
  };

  // ✅ Parse border style
  const parseBorder = (border) => {
    if (!border) return {};

    const borderStyle = {};
    const sides = ['top', 'right', 'bottom', 'left'];

    sides.forEach(side => {
      if (border[side]?.style) {
        const width = border[side].style === 'thin' ? '1px' :
          border[side].style === 'medium' ? '2px' :
            border[side].style === 'thick' ? '3px' : '1px';

        const color = border[side].color?.argb
          ? argbToRgb(border[side].color.argb)
          : '#000';

        borderStyle[`border${side.charAt(0).toUpperCase() + side.slice(1)}`] =
          `${width} solid ${color}`;
      }
    });

    return borderStyle;
  };

  // Detect nếu cell là số
  const isNumericCell = (value) => {
    if (value === null || value === undefined || value === '') return false;
    const num = Number(value);
    return !isNaN(num) && value !== '';
  };

  const isTracking = (value) => {
    if (value === null || value === undefined) return false;
    const str = String(value).trim();

    if (/^[A-Z0-9]{12,}$/i.test(str)) return true;
    // if (/^0[02467]\d{8}$/.test(str)) return true;
    return false;
  };

  // Detect phone number (VN)
  const isPhoneNumber = (value) => {
    if (typeof value !== 'string' && typeof value !== 'number') return false;

    const str = String(value).replace(/\s+/g, '');

    // 0xxxxxxxxx | 84xxxxxxxxx | +84xxxxxxxxx
    return /^(0\d{9}|84\d{9}|\+84\d{9})$/.test(str);
  };

  const formatCellValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    // CASE SĐT
    if (isPhoneNumber(value)) {
      return String(value);
    }

    if (isNumericCell(value) && !isTracking(value)) {
      const num = Number(value);
      if (!Number.isNaN(num)) {
        const rounded = Number(num.toFixed(3));


        return rounded.toLocaleString('vi-VN', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 3,
        });
      }
    }
    if (isDate(value)) {
      return String(formatDate(value))
    }

    return String(value);
  };
  const isDate = (value) => {
    if (!value || typeof value === 'boolean') return false;

    // Loại bỏ số thuần (timestamp, mã đơn hàng, v.v.)
    if (typeof value === 'number') return false;

    const str = String(value).trim();

    // Phải match format ISO hoặc date string rõ ràng
    const datePatterns = [
      /^\d{4}-\d{2}-\d{2}$/,                         // 2026-02-23
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/,              // 2026-02-23T07:04:54.892Z
      /^\d{2}\/\d{2}\/\d{4}$/,                        // 23/02/2026
      /^\d{2}-\d{2}-\d{4}$/,                          // 23-02-2026
    ];

    const isMatchPattern = datePatterns.some(pattern => pattern.test(str));
    if (!isMatchPattern) return false;

    // Kiểm tra date hợp lệ thật sự
    const date = new Date(value);
    return !isNaN(date.getTime());
  };

  const formatDate = (value) => {
    if (!isDate(value)) return value;
    const date = new Date(value);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  };

  const getCellStyle = (rowIndex, colIndex, value) => {
    let baseMinWidth = 60
    const raw = value === null || value === undefined ? '' : String(value).trim();
    const isLongText = !isNumericCell(value)
      && !isPhoneNumber(value)
      && !isTracking(value)
      && !isDate(value);
    if (isLongText) {
      const len = raw.length;
      if (len > 80 && value !== "") {
        baseMinWidth = 320;
      } else if (len > 40) {
        baseMinWidth = 220;
      }
      else if (len > 7 && len <= 15) {
        baseMinWidth = 100;
      }
    }
    const style = {
      padding: '8px',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      minWidth: `${baseMinWidth}px`,
    };

    const cellStyle = data?.styles?.[rowIndex]?.[colIndex];

    if (cellStyle) {
      // Font styles
      if (cellStyle.font) {
        if (cellStyle.font.bold) {
          style.fontWeight = 'bold';
        }
        if (cellStyle.font.size) {
          style.fontSize = `${cellStyle.font.size}px`;
        }
        if (cellStyle.font.color?.argb) {
          style.color = argbToRgb(cellStyle.font.color.argb);
        }
        if (cellStyle.font.name) {
          style.fontFamily = cellStyle.font.name;
        }
        if (cellStyle.font.italic) {
          style.fontStyle = 'italic';
        }
        if (cellStyle.font.underline) {
          style.textDecoration = 'underline';
        }
      }

      // Fill (background color)
      if (cellStyle.fill?.fgColor?.argb) {
        style.backgroundColor = argbToRgb(cellStyle.fill.fgColor.argb);
      }

      // Border
      if (cellStyle.border) {
        Object.assign(style, parseBorder(cellStyle.border));
      }

      // Alignment
      if (cellStyle.alignment) {
        if (cellStyle.alignment.horizontal) {
          const alignMap = {
            'left': 'left',
            'center': 'center',
            'right': 'right',
            'justify': 'justify'
          };
          style.textAlign = alignMap[cellStyle.alignment.horizontal] || 'left';
        }
        if (cellStyle.alignment.vertical) {
          const vAlignMap = {
            'top': 'top',
            'middle': 'middle',
            'bottom': 'bottom'
          };
          style.verticalAlign = vAlignMap[cellStyle.alignment.vertical] || 'middle';
        }
      }
    } else {
      // Default border nếu không có style
      style.border = '1px solid #ddd';
      style.textAlign = 'left';
      style.verticalAlign = 'middle';
    }
    // Override: Nếu là số, căn phải (trừ khi đã có alignment từ Excel)
    if (isNumericCell(value) && !(isPhoneNumber(value) || isTracking(value) || isDate(value)) && !cellStyle?.alignment?.horizontal) {
      style.textAlign = 'right';
      style.fontVariantNumeric = 'tabular-nums';
    }
    if (isTracking(value)) {
      style.textAlign = 'center';
    }
    return style;
  };

  const renderCell = (rowIndex, colIndex, value) => {
    const key = `${rowIndex}-${colIndex}`;
    const mergeInfo = mergedCellsMap.get(key);

    if (mergeInfo?.skip) {
      return null;
    }

    const cellStyle = getCellStyle(rowIndex, colIndex, value);
    const displayValue = formatCellValue(value);

    return (
      <td
        key={key}
        style={cellStyle}
        rowSpan={mergeInfo?.rowSpan || 1}
        colSpan={mergeInfo?.colSpan || 1}
      >
        {displayValue}
      </td>
    );
  };

  const tableWrapperRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);

  // Đo scrollWidth sau khi data render xong
  useEffect(() => {
    const timer = setTimeout(() => {
      if (tableWrapperRef.current) {
        setTableScrollWidth(tableWrapperRef.current.scrollWidth);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [data]);

  // Sync scroll: table → bottom bar
  const handleTableScroll = (e) => {
    if (bottomScrollRef.current) {
      bottomScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  // Sync scroll: bottom bar → table
  const handleBottomScroll = (e) => {
    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  return (
    <div style={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Vùng nội dung cuộn dọc + ngang nhưng ẩn scrollbar ngang */}
      <div
        ref={tableWrapperRef}
        onScroll={handleTableScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'scroll',
          // Ẩn scrollbar ngang gốc
          msOverflowStyle: 'none',
        }}
      >
        <style>{`
        .table-wrapper::-webkit-scrollbar-horizontal { display: none; }
        .table-wrapper { scrollbar-width: none; }
      `}</style>
        <title>Xem tài liệu</title>
        <meta name="description" content="Xem tài liệu trực tiếp trên trình duyệt" />
        <link rel="canonical" href="https://docviewer.top" />
        {!id && (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: '#666',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <p>⚠️ Không tìm thấy id!</p>
          </div>
        )}

        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '60px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #3498db',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }}></div>
            <p style={{ color: '#666' }}>Đang tải dữ liệu...</p>
            <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          </div>
        )}

        {/* {error && (
          <div style={{
            color: '#721c24',
            padding: '20px',
            background: '#f8d7da',
            borderRadius: '8px',
            border: '1px solid #f5c6cb',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <strong>Lỗi:</strong> {String(error)}
            <p>Vui lòng kiểm tra thông báo cho IT</p>
          </div>
        )} */}

        {data?.rows && data.rows.length === 0 && (
          <div
            style={{
              padding: '40px',
              textAlign: 'center',
              color: '#666',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <p>📄 File không có dữ liệu.</p>
          </div>
        )}

        {data?.rows && data.rows.length > 0 && (
          <div className="table-wrapper" style={{ backgroundColor: 'white' }}>
            <table style={{
              borderCollapse: 'collapse',
              width: '100%',
              fontSize: '14px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            }}>
              <tbody>
                {data.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, colIndex) => renderCell(rowIndex, colIndex, cell))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data?.rows && data.rows.length > 0 && (
        <div
          ref={bottomScrollRef}
          onScroll={handleBottomScroll}
          style={{
            height: '12px',
            overflowX: 'auto',
            overflowY: 'hidden',
            borderTop: '1px solid #ddd',
            background: '#f5f5f5',
            flexShrink: 0,
          }}
        >
          {/* Div rỗng để tạo scrollable width */}
          <div style={{ width: tableScrollWidth, height: 1 }} />
        </div>
      )}
    </div>
  );
};


export default ViewExcel;